import React from 'react'
import { formatPeople } from '../../utils'
import { Card } from '../Card'
import { Meaning, Metric, Note } from '../CardParts'
import styles from './engagement.module.sass'
import { Figure, ShareBar, SmallPrint } from './parts'
import type { EngagementCardProps } from './parts'
import { cappedShare, changeFrom, exceedsEnrolled, inTen } from './utils'

export const CoachingCard = ({ data, points, since, footer, highlighted, onAbout }: EngagementCardProps) => {
  const { enrolled, early_days } = data.people
  const { contacted, booked, coached } = data.engagement.coaching
  const share = cappedShare(coached, enrolled)
  const overEnrolled = exceedsEnrolled(coached, enrolled)
  const steps = [
    { label: 'Enrolled', count: enrolled },
    { label: 'Contacted', count: contacted },
    { label: 'Booked', count: booked },
    { label: 'Coached', count: coached },
  ]

  return (
    <Card
      question="1:1 coaching"
      title={coachingTitle({ share, overEnrolled, contacted, earlyDays: early_days, since })}
      onAbout={onAbout}
      highlighted={highlighted}
      footer={{ text: footer, chip: 'Everyone enrolled included' }}
    >
      <Metric text={`Coaching · share of ${enrolled} enrolled people coached at least once`} />
      <CoachedFigure share={share} coached={coached} overEnrolled={overEnrolled} change={overEnrolled ? null : changeFrom(points, 'coaching', enrolled)} />
      <CoachedBar share={share} overEnrolled={overEnrolled} />
      <Steps steps={steps} />
      <Note text={notBookedNote(contacted - booked)} />
      <Meaning text="One-to-one sessions with a qualified Wealthbit coach, by phone or video, about whatever the person wants help with." />
    </Card>
  )
}

type TitleInput = { share: number; overEnrolled: boolean; contacted: number; earlyDays: boolean; since: string }

// Never "All enrolled people…": a full share can include people who have left.
const coachingTitle = ({ share, overEnrolled, contacted, earlyDays, since }: TitleInput): string => {
  if (earlyDays && contacted > 0) return 'Coaching has started with the first people to onboard'
  if (!overEnrolled && share >= 10 && share < 100) return `${inTen(share)} enrolled people have had a coaching session`
  return `Coaching ${since}`
}

const notBookedNote = (waiting: number): string | null => {
  if (waiting <= 0) return null
  if (waiting === 1) return '1 person has been contacted but hasn’t booked a session yet.'
  return `${waiting} people have been contacted but haven’t booked a session yet.`
}

type CoachedFigureProps = { share: number; coached: number; overEnrolled: boolean; change: string | null }

// More coached than enrolled now: the count, since a share would pass 100%.
const CoachedFigure = ({ share, coached, overEnrolled, change }: CoachedFigureProps) => {
  if (overEnrolled) return <Figure value={String(coached)} label="people coached, including some who have since left" change={null} />
  return <Figure value={`${share}%`} label={`${formatPeople(coached)} coached`} change={change} />
}

// A bar full to "100% of enrolled" would say everyone was coached.
const CoachedBar = ({ share, overEnrolled }: { share: number; overEnrolled: boolean }) => {
  if (overEnrolled) return <SmallPrint text={'More people have been coached than are enrolled now, so there\u2019s no share of enrolled to show.'} />
  return <ShareBar share={share} channel="coaching" />
}

type Step = { label: string; count: number }

// Counts, not shares: coaching records aren't matched one-to-one with the enrolment list.
const Steps = ({ steps }: { steps: Step[] }) => {
  const items = steps.map((step, index) => <StepItem key={step.label} step={step} last={index === steps.length - 1} />)

  return (
    <div className={styles.block}>
      <p className={styles.blockTitle}>People at each step, from enrolled to coached</p>
      <ol className={styles.steps}>{items}</ol>
    </div>
  )
}

const StepItem = ({ step, last }: { step: Step; last: boolean }) => (
  <li className={last ? `${styles.step} ${styles.stepFinal}` : styles.step}>
    <span className={styles.stepLabel}>{step.label}</span>
    <span className={styles.stepCount}>{step.count}</span>
  </li>
)
